import type { JobIconName } from '../lib/jobTypes'

interface JobIconProps {
  name: JobIconName
  size?: number
}

/**
 * Job-type icons for the opening screen.
 *
 * Drawn on a shared 24-grid with a single stroke weight so a row of tiles
 * reads as one set. Deliberately literal — a brake disc, a tyre, a spanner —
 * because customers scan for the shape of the thing they need, not for an
 * abstract glyph.
 */
export function JobIcon({ name, size = 28 }: JobIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name] ?? PATHS.other}
    </svg>
  )
}

const PATHS: Record<JobIconName, React.ReactNode> = {
  // Spanner over a cog.
  servicing: (
    <>
      <circle cx="9" cy="9" r="3.2" />
      <path d="M9 2.6v1.6M9 13.8v1.6M2.6 9h1.6M13.8 9h1.6M4.5 4.5l1.1 1.1M12.4 12.4l1.1 1.1M13.5 4.5l-1.1 1.1M5.6 12.4l-1.1 1.1" />
      <path d="M21 14.2a3.3 3.3 0 0 1-4.4 3.1l-3 3a1.6 1.6 0 0 1-2.3-2.3l3-3A3.3 3.3 0 0 1 17.6 11l-1.7 1.7 1.8 1.8L19.4 13c.9.3 1.6 1.2 1.6 2.2z" />
    </>
  ),

  // Clipboard with a pass tick.
  mot: (
    <>
      <path d="M8 4H6.5A1.5 1.5 0 0 0 5 5.5v14A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-14A1.5 1.5 0 0 0 17.5 4H16" />
      <rect x="8" y="2.5" width="8" height="3.2" rx="1" />
      <path d="m8.8 13.4 2.2 2.2 4.2-4.4" />
    </>
  ),

  // Brake disc with caliper.
  brakes: (
    <>
      <circle cx="11" cy="12" r="7.5" />
      <circle cx="11" cy="12" r="2.6" />
      <path d="M11 4.5v2M11 17.5v2M3.5 12h2M16.5 12h2" />
      <path d="M18.5 7.2A2 2 0 0 1 21 9.1v5.8a2 2 0 0 1-2.5 1.9" />
    </>
  ),

  // Tyre with tread.
  tyres: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="M12 3.5v4.9M12 15.6v4.9M3.5 12h4.9M15.6 12h4.9" />
    </>
  ),

  // Snowflake for air conditioning.
  aircon: (
    <>
      <path d="M12 2.8v18.4M4 7.4l16 9.2M20 7.4 4 16.6" />
      <path d="M9.6 4.6 12 6.9l2.4-2.3M9.6 19.4 12 17.1l2.4 2.3" />
      <path d="m4.6 10.6.2 3.2 2.9-1.4M19.4 13.4l-.2-3.2-2.9 1.4" />
    </>
  ),

  // Plug-in scan tool.
  diagnostics: (
    <>
      <rect x="3" y="4.5" width="13" height="12" rx="2" />
      <path d="M6 11.5h1.8l1.3-2.6 1.6 4.4 1.3-2.6h1.4" />
      <path d="M9.5 16.5v3M6.5 19.5h6M19 8.5v5.5a2.5 2.5 0 0 1-2.5 2.5H16" />
      <path d="M17.6 4.5v3M20.4 4.5v3" />
    </>
  ),

  battery: (
    <>
      <rect x="2.5" y="7.5" width="19" height="10" rx="2" />
      <path d="M6.5 5.5v2M17.5 5.5v2" />
      <path d="m12.6 10-2 3h3l-2 3" />
      <path d="M5.5 11v3" />
    </>
  ),

  exhaust: (
    <>
      <path d="M3 14.5h9.5a3 3 0 0 1 0 6H5a2 2 0 0 1-2-2v-4z" />
      <path d="M6.5 14.5v6" />
      <path d="M15.5 17.5h3" />
      <path d="M14 9.5c2 0 2-2.5 4-2.5M13 5.5c2.5 0 2.5-2 5-2" />
    </>
  ),

  clutch: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 3.5v17M3.5 12h17" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),

  suspension: (
    <>
      <path d="M12 2.5v3M12 18.5v3" />
      <path d="M8.5 6h7M8.5 18h7" />
      <path d="M9 8.5c2 0 4 1 6 1s2-1 0-1M9 12c2 0 4 1 6 1s2-1 0-1M9 15.5c2 0 4 1 6 1s2-1 0-1" />
    </>
  ),

  windscreen: (
    <>
      <path d="M3.5 14.5 6 7a2 2 0 0 1 1.9-1.4h8.2A2 2 0 0 1 18 7l2.5 7.5" />
      <path d="M2.5 14.5h19a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-19a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1z" />
      <path d="m12 6.5-.7 4M14.5 9.5l1.6 1.4-2.2.6.8 1.9" />
    </>
  ),

  // Wheel alignment: wheel between guide lines.
  wheels: (
    <>
      <circle cx="12" cy="12" r="5.5" />
      <circle cx="12" cy="12" r="1.6" />
      <path d="M4 4.5v15M20 4.5v15" />
      <path d="m12 6.5 1.6 3M12 17.5l-1.6-3" />
    </>
  ),

  repairs: (
    <>
      <path d="M14.6 3.4a5 5 0 0 0-6.2 6.2L3.6 14.4a2 2 0 0 0 2.8 2.8l4.8-4.8a5 5 0 0 0 6.2-6.2l-2.6 2.6-2.4-.6-.6-2.4 2.8-2.4z" />
      <path d="M14.5 14.5 20 20" />
    </>
  ),

  other: (
    <>
      <path d="M20.4 5.6a4.6 4.6 0 0 1-5.8 5.8L6.9 19.1a2.1 2.1 0 0 1-3-3l7.7-7.7a4.6 4.6 0 0 1 5.8-5.8l-2.9 2.9.7 2.5 2.5.7 2.7-3.1z" />
    </>
  ),
}
